import * as THREE from 'three';
import earth from '../../../Assets/Images/Planets/2k_earth_daymap.jpg';
import mercury from '../../../Assets/Images/Planets/2k_mercury.jpg';
import mars from '../../../Assets/Images/Planets/2k_mars.jpg';
import venus from '../../../Assets/Images/Planets/2k_venus_surface.jpg';
import jupiter from '../../../Assets/Images/Planets/2k_jupiter.jpg';
import saturn from '../../../Assets/Images/Planets/2k_saturn.jpg';
import saturnRing from '../../../Assets/Images/Planets/2k_saturn_ring_alpha.png'
import uranus from '../../../Assets/Images/Planets/2k_uranus.jpg';
import neptune from '../../../Assets/Images/Planets/2k_neptune.jpg';
import Sun from '../../../Assets/Images/Planets/2k_sun.jpg';
import Asteroid from '../../../Assets/GLTF/Astreoid-1.glb';
import { scene } from './Initial3DSetup';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import Begok from 'three/examples/fonts/droid/droid_sans_regular.typeface.json';
import { setPlanet } from '../AddImages';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { getLoadingManager } from '../../Utils/loadManager';


export const planets = new THREE.Group();
export const planetLabels = new THREE.Group();

// The letters are cut to a fifth of their cap height — the proportion ordinary signage
// lettering is cut to. The depth is worked out from the size rather than given a number
// of its own because the two only read right together, and retuning the size alone would
// otherwise quietly change how deep the letters look.
const LABEL_SIZE = 2;
const LABEL_DEPTH_RATIO = 0.2;

const TextParam = {
    font: null,
    size: LABEL_SIZE,
    depth: LABEL_SIZE * LABEL_DEPTH_RATIO,
    curveSegments: 4,
    bevelThickness: 0.1,
    bevelSize: 0.1,
    bevelEnabled: true
}
export const texts = [];

// ─── Saturn's debris belt ─────────────────────────────────────────────────────
//
// 2500 rocks scattered through the ring. Built as one InstancedMesh this was a single
// bounding sphere for the whole belt, so any part of it in frame submitted every
// instance: 2500 x 49152 triangles, 122.9 million of them, because rock.007 is the
// only high-poly rock in Astreoid-1.glb and it is the one the belt was built from.
// Split into angular sectors, each carrying its own sphere, only the sectors actually
// in frame are submitted. And because the sectors cross the edge of the screen one at
// a time as the camera turns, the belt is revealed across several frames instead of
// arriving whole the instant any part of it comes into view.
//
// The instances are placed by drawing the same random numbers, in the same order, as
// the single-mesh version drew them, so the belt sits exactly where it sat before.
// This changes what gets drawn, not where anything is.
const BELT_INSTANCES = 2500;
const BELT_SECTORS = 16;

// A rock is 0.05 x a bounding box of about 3 units, so roughly 0.15 units across. At
// the home camera, ~154 units from Saturn, that is about two pixels on screen, and the
// file's blunt rocks cannot be told from rock.007 at that size. These are the distances
// — from the camera to Saturn's centre — at which the whole belt drops a tier, each one
// still only a few pixels a rock, so the swap is not visible. They are the knobs to turn
// if the belt ever reads as too blunt.
const BELT_LOD_DISTANCES = [12, 60];

/** Random in [min, max), or in [0, 1) when min is not given. */
function r(min, max) {
    const random = min ? min + Math.random() * (max - min) : Math.random();

    return random;
}

function trianglesIn(geometry) {
    return geometry.index ? geometry.index.count / 3 : geometry.attributes.position.count / 3;
}

function buildAsteroidBelt(gltf, planetMesh, ringInner, ringOuter) {

    const rocks = gltf.scene.children.filter(child => child.geometry);
    const detailed = rocks[0];

    // The other eight rocks of the file, bluntest first. They are not decimations of
    // rock.007 — they are eight separate rocks whose bounding boxes all sit within a
    // factor of two of each other — so a distant sector still reads as a rock of the
    // right size, just a simpler one. Nothing new is authored for the tiers: they were
    // already in the file that was being loaded for the one rock.
    const blunter = rocks.slice(1).sort((a, b) => trianglesIn(a.geometry) - trianglesIn(b.geometry));
    const geometries = [
        detailed.geometry,
        blunter[0] && blunter[0].geometry,
        blunter[1] && blunter[1].geometry
    ].filter(Boolean);

    // Scaled on the geometry rather than on each instance, as the single-mesh version
    // did. Guarded because these are the loader's own geometries and a second pass
    // would compound the scale. A sector swapping tiers therefore swaps between three
    // rocks that are all on the same footing.
    geometries.forEach(geometry => {
        if (!geometry.userData.beltScaled) {
            geometry.scale(0.05, 0.05, 0.05);
            geometry.userData.beltScaled = true;
        }
    });

    // Placement first, bucketing second. Drawing every random number in one pass, in
    // the original order, is what keeps the belt still if the sector count is changed.
    const placements = [];
    for (let i = 0; i < BELT_INSTANCES; i++) {
        const theta = 2 * Math.PI * Math.random(); // Random angle
        // Scattered across the ring itself, not outside it. Local to the mesh, so this
        // is the same footing the ring's own radii are on.
        const distance = ringInner + (ringOuter - ringInner) * Math.random();
        const y = Math.random() * 0.2; // Small random height to give some thickness
        placements.push({
            theta,
            x: distance * Math.cos(theta),
            y,
            z: distance * Math.sin(theta),
            scale: r(),
            rotation: [
                r(5 / Math.PI, 350 / Math.PI),
                r(5 / Math.PI, 350 / Math.PI),
                r(5 / Math.PI, 350 / Math.PI)
            ]
        });
    }

    // A sector is a slice of angle, and its bounding sphere has to cover its own
    // instances and nothing else — that is the whole point of the split, so the
    // bucketing is by angle and not by anything the sphere would blur together.
    const buckets = Array.from({ length: BELT_SECTORS }, () => []);
    placements.forEach(placement => {
        const sector = Math.min(
            BELT_SECTORS - 1,
            Math.floor((placement.theta / (2 * Math.PI)) * BELT_SECTORS)
        );
        buckets[sector].push(placement);
    });

    const belt = new THREE.Group();
    belt.name = 'asteroids';
    // Turns with the ring, at half the planet's rate, for the same reason. Held on the
    // group rather than on each sector so the belt turns as one body: Animate.js spins
    // whichever of the mesh's children carries this, and that is now the group.
    belt.userData.spinFactor = -0.5;

    const dummy = new THREE.Object3D();
    const sectors = [];

    buckets.forEach((bucket, index) => {
        if (bucket.length === 0) return;

        // The material is shared. Nothing tints or fades a sector on its own, so a
        // clone per sector would buy nothing and cost sixteen more materials.
        const sector = new THREE.InstancedMesh(geometries[0], detailed.material, bucket.length);
        sector.name = 'asteroids_' + index;
        // Nothing in this scene casts or receives — the shadow map is off. Set anyway,
        // so that turning it back on does not quietly put 2500 rocks through a shadow
        // pass each frame.
        sector.castShadow = false;
        sector.receiveShadow = false;

        bucket.forEach((placement, i) => {
            dummy.position.set(placement.x, placement.y, placement.z);
            dummy.scale.set(placement.scale, placement.scale, placement.scale);
            dummy.rotation.set(placement.rotation[0], placement.rotation[1], placement.rotation[2]);
            dummy.updateMatrix();
            sector.setMatrixAt(i, dummy.matrix);
        });

        sector.instanceMatrix.needsUpdate = true;
        // The sphere is what three culls against, and it is measured from the instance
        // matrices, so it has to be computed after they are set — and recomputed after
        // a tier swap replaces the geometry it is measured from.
        sector.computeBoundingSphere();
        sector.userData.tier = 0;

        sectors.push(sector);
        belt.add(sector);
    });

    // A child of Saturn's mesh, not of the scene. The orbit position lives on the
    // planet group, and it is the mesh that is tilted into the ring's plane, so
    // parenting here inherits both. Added to the scene instead, the belt is handed the
    // mesh's *local* position — always zero — and lands on the sun.
    planetMesh.add(belt);

    watchBeltTiers(belt, sectors, geometries);
}

/**
 * Drops the belt to a blunter rock as the camera pulls away from Saturn, and lifts it
 * back to the detailed one as the camera comes closer.
 *
 * One distance decides every sector, measured from the camera to the belt's own centre
 * — not, as this once did, from the camera to each sector's bounding sphere in turn.
 * That per-sector measure put the near threshold inside the belt's own radius range: a
 * camera landed on Saturn stands 16.5 units out from a ring that runs 5 to 10, so the
 * sector centres sat anywhere from 6.5 to 26.5 away and the 12-unit line fell across the
 * middle of them. A wedge of the belt read as detailed and the rest as blunt, and because
 * the belt turns, sector after sector swept through that wedge and swapped geometry as it
 * went — the belt visibly changing LOD, a sector every second or so, for as long as
 * Saturn was on screen. A distance to the belt's centre cannot do that: the belt turns
 * about that centre, so the distance does not change at all while the camera holds still.
 *
 * Sector culling is untouched and still per-sector — each still carries its own bounding
 * sphere, which is what keeps 2500 rocks from being submitted as one object. Only which
 * rock gets drawn moved.
 *
 * Hung off the scene's before-render hook, which is the one place the camera can be read
 * without this module reaching into the animation loop. three calls it after the scene
 * graph's world matrices have been updated and before the render state is built, so the
 * belt's world position and each sector's matrix are current and a geometry swap lands
 * before the renderer has begun walking the objects, rather than in the middle of a draw.
 */
function watchBeltTiers(belt, sectors, geometries) {

    const worldCenter = new THREE.Vector3();
    const lastTier = geometries.length - 1;

    scene.onBeforeRender = (renderer, rendering, camera) => {

        belt.getWorldPosition(worldCenter);
        const distance = camera.position.distanceTo(worldCenter);

        let tier = lastTier;
        for (let i = 0; i < lastTier; i++) {
            if (distance < BELT_LOD_DISTANCES[i]) { tier = i; break; }
        }

        sectors.forEach(sector => {
            if (tier === sector.userData.tier) return;

            sector.geometry = geometries[tier];
            sector.computeBoundingSphere();
            sector.userData.tier = tier;
        });
    };
}

export function loadCelestialBodies() {

    planets.name = 'Planets';
    planetLabels.name = 'PlanetLabels'

    const sunRad = 14.5;
    const radiusOffset = 0.007;

    // Saturn's ring, and the band its debris is scattered through. Shared, because the
    // belt is drawn from the ring's own radii rather than from numbers of its own: the
    // two drifting apart is exactly what leaves debris outside the ring.
    const RING_INNER = 5;
    const RING_OUTER = 10;

    // The ring reaches well past the sphere it circles, and a landed body is framed on its
    // sphere — so Saturn arrives filling the frame with its ring running off all four edges.
    // That is deliberate, not the same fault as a body that misses the frame: the sphere is
    // what is being looked at, and framing the ring instead would leave Saturn visibly
    // smaller than every other body for scenery's sake.

    // Planets data
    const planetInfo = [
        { name: 'Sun', texture: Sun, distance: 0, size: 14.5, planetLabel: 'Caricatures', scale: 1, radiusOffset: 0.35 },
        { name: 'Mercury', texture: mercury, distance: 5.8 + sunRad, size: 0.196, planetLabel: 'Comics', scale: 0.00357 * 4, radiusOffset: 0 },
        { name: 'Venus', texture: venus, distance: 10.8 + sunRad, size: 0.484, planetLabel: 'Nature', scale: 0.00869 * 4, radiusOffset: 0 },
        { name: 'Earth', texture: earth, distance: 15 + sunRad, size: 0.5104, planetLabel: 'Portraits', scale: 0.00917 * 4, radiusOffset: 0 },
        { name: 'Mars', texture: mars, distance: 22.5 + sunRad, size: 0.276, planetLabel: 'AboutMe', scale: 0.00489 * 4, radiusOffset: 0 },
        { name: 'Jupiter', texture: jupiter, distance: 75 + sunRad, size: 6.4, planetLabel: 'Web3D_VR', scale: 0.1 * 4, radiusOffset: 0.05 },
        { name: 'Saturn', texture: saturn, distance: 140 + sunRad, size: 4.72, planetLabel: 'Logo Design', scale: 0.08663 * 4, radiusOffset: 0.05 },
        { name: 'Uranus', texture: uranus, distance: 290 + sunRad, size: 2.4, planetLabel: 'Under Construction', scale: 0.03663 * 4, radiusOffset: 0 },
        { name: 'Neptune', texture: neptune, distance: 450 + sunRad, size: 2, planetLabel: 'ADMIN', scale: 0.03591 * 4, radiusOffset: 0 }
    ];

    const angleOffset = 360 / planetInfo.length;
    let angle = 1;

    loadAllPlanets();

    function loadAllPlanets() {
        const texLoader = new THREE.TextureLoader(getLoadingManager());
        const fontLoader = new FontLoader();
        TextParam.font = fontLoader.parse(Begok);

        const sunLight = new THREE.PointLight(0x404040, 1000, 500, 0.8); // bright from the center of the sun
        sunLight.name = 'Sunlight';
        sunLight.position.set(0, 0, 0);
        sunLight.castShadow = true;

        scene.add(sunLight);
        scene.add(planets);

        // Create planets
        planetInfo.forEach(p => {

            const planet = addPlanet(p);

            setPlanet(planet);

            planets.add(planet);

        });


        function addPlanet(p) {
            const planet = new THREE.Group();
            planet.name = p.name;

            const geometry = new THREE.SphereGeometry(p.size, 64, 64);
            geometry.computeBoundingSphere();

            const texture = texLoader.load(p.texture);

            const material = p.name === 'Sun' ? new THREE.MeshBasicMaterial({ map: texture, emissive: 0x2222ff }) :
                new THREE.MeshStandardMaterial({ map: texture });

            const planet_mesh = new THREE.Mesh(geometry, material);

            planet_mesh.receiveShadow = p.name === 'Sun' ? false : true;

            angle *= angleOffset;
            planet.position.x = p.distance * Math.cos(angle);
            planet.position.z = p.distance * Math.sin(angle);
            planet.userData.spotlightIntensity = 4.959 * p.size;

            const planetLabel = createText(TextParam, p.planetLabel, p.size);
            planetLabel.userData.group = planet.name;
            planetLabel.userData.isLabel = true;

            planet.userData.labelScale = p.scale;
            planet.userData.labelPosition = new THREE.Vector3().copy(planetLabel.position);
            planet.userData.radiusOffset = p.size * radiusOffset - p.radiusOffset;
            planet.userData.imgGroupLabel = p.planetLabel;
            planet.userData.focus = false;
        

            planet_mesh.name = p.name + '_mesh';
            planet_mesh.userData.group = planet.name;

            planet.userData.radius = p.size;
            planet.userData.isFocused = false;
            planet.userData.isPlanet = true;
           
            //Add Satrun's Ring
            if (p.name === 'Saturn') {
                console.log('saturn ring name:', p.size)
                addSaturnRing(planet_mesh);
                planet_mesh.rotateX(35);
            }

            planet.add(planet_mesh);

            planet.add(planetLabel);

            return planet;
        }

        function addSaturnRing(mesh) {
            //ADD RING
            const ringGeometry = new THREE.CylinderGeometry(RING_INNER, RING_OUTER, 0.01, 128, 1, true);  // Inner radius, outer radius, and segments

            const ringTexture = texLoader.load(saturnRing);

            ringTexture.rotation = Math.PI / 2;

            const ringMaterial = new THREE.MeshBasicMaterial({ map: ringTexture, side: THREE.DoubleSide });

            ringMaterial.transparent = true;
            ringMaterial.opacity = 0.85;
            ringMaterial.shadowSide = THREE.DoubleSide;

            const ring = new THREE.Mesh(ringGeometry, ringMaterial);
            ring.name = 'SaturnRing';
            ring.receiveShadow = true;

            // It hangs off the mesh, so it inherits the planet's whole spin; handing half
            // of it back leaves the ring turning at half the planet's rate. Animate.js
            // reads this — the number lives here, next to the thing it describes.
            ring.userData.spinFactor = -0.5;

            mesh.add(ring);

            //Asteroids if needed
             const debriLoader = new GLTFLoader(getLoadingManager());
             debriLoader.load(Asteroid, (obj) => buildAsteroidBelt(obj, mesh, RING_INNER, RING_OUTER));
        }
    }
}



function createText(param, text, radius) {

    const textGeo = new TextGeometry(text, {
        font: param.font,
        size: param.size,
        // `depth`, not `height`. three renamed the extrusion parameter in r132 and the
        // old name is not read at all, so passing it silently left TextGeometry on its
        // own 50-unit default — five times taller than these letters, and the same
        // whatever TextParam.depth said.
        depth: param.depth,
        curveSegments: param.curveSegments,
        bevelThickness: param.bevelThickness,
        bevelSize: param.bevelSize,
        bevelEnabled: param.bevelEnabled
    });
    centerGeometry(textGeo);
    //textGeo.translate(0, offsetY, 0);

    // The stamp's own two colours, which are what the NavBar's head word wears: `#FF0006` is the
    // red `logo-stamp.png` is lettered in, `#645075` the violet it is 74% of. The label and that
    // row name the same six categories and are read in the same frame, so they take the same pair.
    //
    // Unlit, and that is the part that changes the look: these were `MeshPhongMaterial` and took
    // the scene's lights, which in here is one flashlight the visitor aims and a sun a long way
    // off. A caption that dims as the beam swings off it is the one thing a caption must not do —
    // the same argument the row's own materials carry. `transparent` is carried over from the lit
    // pair so nothing about how the label is drawn changes beside its colour.
    //
    // Two materials for the reason the row's letters have two: an extrusion is cut into groups —
    // the faces first, then the sides it was extruded through — and on a material that takes no
    // light that second band is the only thing that says a letter has any thickness at all.
    const materials = [
        new THREE.MeshBasicMaterial({ color: 0xff0006, transparent: true }), // front
        new THREE.MeshBasicMaterial({ color: 0x645075, transparent: true }) // side
    ];

    const textMesh = new THREE.Mesh(textGeo, materials);
    textMesh.receiveShadow = true;
    textMesh.name = text + '_mesh'
    textMesh.layers.set(5);
    textMesh.position.setY(radius + 2);

    return textMesh;

}

function centerGeometry(textGeo) {

    textGeo.computeBoundingBox();

    const offsetX = (textGeo.boundingBox.max.x - textGeo.boundingBox.min.x) / 2;
    const offsetY = (textGeo.boundingBox.max.y - textGeo.boundingBox.min.y) / 2;
    const offsetZ = (textGeo.boundingBox.max.z - textGeo.boundingBox.min.z) / 2;

    textGeo.translate(-offsetX, -offsetY, -offsetZ);
}



